import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Action, Store } from '@ngrx/store';
import { EMPTY, Observable, of, switchMap } from 'rxjs';
import { map, tap } from 'rxjs/operators';
import { TableStoreService } from 'app/services/table-store.service';
import { tableFiltersAndMetaActions, tableStoreActions, tableWidthActions } from 'app/actions/table-store-data';
import * as fromTableStore from 'app/reducers/table-store-data';
import * as fromTable from 'app/reducers/table-store-data';
import { TablesWidthData } from 'app/models/reducers/table-store';
import { SapTablesFilters } from 'app/models/reducers/sap';
import { concatLatestFrom } from '@ngrx/operators';

@Injectable()
export class TableStoreEffects {
  private actions$: Actions = inject(Actions);
  private store: Store = inject(Store);
  private tableStoreService: TableStoreService = inject(TableStoreService);

  getDataFromLocalStorage$: Observable<Action> = createEffect(() => this.actions$.pipe(
    ofType(tableWidthActions.getDataFromLocalStorage),
    map(() => this.tableStoreService.getTableWidthData()),
    switchMap((data: TablesWidthData | null) => data
      ? of(tableWidthActions.getDataFromLocalStorageSuccess({ data }))
      : EMPTY
    )
  ));

  saveTableWidthDataInStore$: Observable<Action> = createEffect(() => this.actions$.pipe(
    ofType(tableWidthActions.saveTableWidthDataInStore),
    concatLatestFrom(() => this.store.select(fromTableStore.selectTablesWidth)),
    map(([, data]: [Action, TablesWidthData]) => tableWidthActions.saveDataInLocalStorage({ data })),
  ));

  saveDataInLocalStorage$: Observable<Action> = createEffect(() => this.actions$.pipe(
    ofType(tableWidthActions.saveDataInLocalStorage),
    tap(action => this.tableStoreService.setTableWidthData(action.data))
  ), { dispatch: false });

  getSapFiltersFromStorage$: Observable<Action> = createEffect(() => this.actions$.pipe(
    ofType(tableFiltersAndMetaActions.getSapFiltersFromStorage),
    map(() => this.tableStoreService.getAllSapFiltersFromStorage()),
    switchMap((data: SapTablesFilters|null) => data
      ? of(tableFiltersAndMetaActions.getSapFiltersFromStorageSuccess({ data }))
      : EMPTY
    ),
  ));

  saveSapFiltersInLocalStorageCases$: Observable<unknown> = createEffect(() => this.actions$.pipe(
    // list of sap actions after witch we save the state of sap filters tables
    ofType(
      tableStoreActions.saveIncidentSapNotificationsState,
      tableStoreActions.saveIncidentSapOrdersState,
      tableStoreActions.saveIncidentSapOrderOperationsState,
      tableStoreActions.saveIncidentSapOrderOperationsEditState,
      tableStoreActions.saveDefaultSapMachineOrderOperationsState,
      tableStoreActions.saveDefaultSapMachineOrderOperationsEditState,
      tableStoreActions.saveMachineDetailsSapNotificationsState,
      tableStoreActions.saveMachineDetailsSapOrdersState,
      tableStoreActions.saveFlatSAPMachineOverviewState,
      tableStoreActions.saveAssetSAPMachineOverviewState,
      tableStoreActions.saveDefaultSapMachineNotificationsState,
    ),
    concatLatestFrom(() => [
      this.store.select(fromTable.selectIncidentSapNotifications),
      this.store.select(fromTable.selectIncidentSapOrders),
      this.store.select(fromTable.selectIncidentSapOrderOperations),
      this.store.select(fromTable.selectIncidentSapOrderOperationsEdit),
      this.store.select(fromTable.selectSapMachinesSapOrderOperations),
      this.store.select(fromTable.selectSapMachinesSapOrderOperationsEdit),
      this.store.select(fromTable.selectMachineDetailsSapNotifications),
      this.store.select(fromTable.selectMachineDetailsSapOrders),
      this.store.select(fromTable.selectSapFlatMachinesOverview),
      this.store.select(fromTable.selectSapAssetMachinesOverview),
      this.store.select(fromTable.selectDefaultSapMachineNotifications),
    ]),
    map((
      // list of data that is saved into storage
      [,
        incidentSapNotifications,
        incidentSapOrders,
        incidentSapOrderOperations,
        incidentSapOrderOperationsEdit,
        sapMachinesSapOrderOperations,
        sapMachinesSapOrderOperationsEdit,
        machineDetailsSapNotifications,
        machineDetailsSapOrders,
        sapFlatMachinesOverview,
        sapAssetMachinesOverview,
        defaultSapMachineNotifications,
      ]
    ) => ({
      incidentSapNotifications,
      incidentSapOrders,
      incidentSapOrderOperations,
      incidentSapOrderOperationsEdit,
      sapMachinesSapOrderOperations,
      sapMachinesSapOrderOperationsEdit,
      machineDetailsSapNotifications,
      machineDetailsSapOrders,
      sapFlatMachinesOverview,
      sapAssetMachinesOverview,
      defaultSapMachineNotifications,
    })),
    tap((filters: SapTablesFilters) => this.tableStoreService.putAllSapFiltersToStorage(filters)
    )), { dispatch: false });
}
